import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CheckCircle,
  ClipboardList,
  Download,
  Edit3,
  Link,
  PlusCircle,
  Search,
  Send,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from '@clareefai/ui';
import PageTitle from '../../components/shared/PageTitle';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import SurveyResponseCard from '../../components/survey/SurveyResponseCard';
import CreateSurveyModal from '../../components/survey/CreateSurveyModal';
import {
  useGetSurveyDashboardQuery,
  useGetSurveyResponsesQuery,
  useGetSurveysQuery,
  useCopySurveyLinkMutation,
  useDeleteSurveyMutation,
} from '../../store/surveyApi';

const STATUS_COLORS = {
  ACTIVE: 'bg-green-100 text-green-700',
  DRAFT: 'bg-gray-100 text-gray-600',
  CLOSED: 'bg-red-100 text-red-700',
};

const SurveyDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | undefined>(undefined);
  const [filterQuery, setFilterQuery] = useState('');
  const [deletingSurveyId, setDeletingSurveyId] = useState<string | null>(null);

  const { data: dashboard, isLoading: dashboardLoading } = useGetSurveyDashboardQuery();
  const { data: responses = [], isLoading: responsesLoading } = useGetSurveyResponsesQuery(selectedSurveyId);
  const { data: surveys = [], isLoading: surveysLoading } = useGetSurveysQuery();
  const [copyLink, { isLoading: copyingLink }] = useCopySurveyLinkMutation();
  const [deleteSurvey, { isLoading: deletingLoading }] = useDeleteSurveyMutation();

  const handleExportCsv = () => {
    const params = selectedSurveyId ? `?surveyId=${selectedSurveyId}` : '';
    const a = document.createElement('a');
    a.href = `/api/vendors/me/surveys/export-csv${params}`;
    a.download = 'survey-responses.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDeleteSurvey = async () => {
    if (!deletingSurveyId) return;
    try {
      await deleteSurvey(deletingSurveyId).unwrap();
      if (selectedSurveyId === deletingSurveyId) setSelectedSurveyId(undefined);
      toast.success(t('surveys.surveyDeleted'));
    } catch {
      toast.error(t('surveys.failedToDelete'));
    } finally {
      setDeletingSurveyId(null);
    }
  };

  const handleCopySurveyLink = async (surveyId?: string) => {
    const id = surveyId ?? selectedSurveyId ?? surveys[0]?.id;
    if (!id) {
      toast.error(t('surveys.createFirst'));
      return;
    }
    try {
      const result = await copyLink(id).unwrap();
      await navigator.clipboard.writeText(result.link);
      toast.success(t('surveys.surveyLinkCopied'));
    } catch {
      toast.error(t('surveys.failedToCopyLink'));
    }
  };

  const statCards = [
    {
      label: t('surveys.statCards.surveysSent'),
      value: dashboard?.surveysSent ?? 0,
      icon: Send,
      sub: dashboard?.responseRate != null ? t('surveys.statCards.responseRate', { rate: dashboard.responseRate }) : null,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: t('surveys.statCards.responsesReceived'),
      value: dashboard?.responsesReceived ?? 0,
      icon: CheckCircle,
      sub: null,
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: t('surveys.statCards.avgNpsScore'),
      value: dashboard?.avgNpsScore != null ? dashboard.avgNpsScore.toFixed(1) : '—',
      icon: BarChart3,
      sub:
        dashboard?.avgNpsScore != null
          ? dashboard.avgNpsScore >= 9
            ? t('surveys.statCards.excellent')
            : dashboard.avgNpsScore >= 7
              ? t('surveys.statCards.good')
              : t('surveys.statCards.needsImprovement')
          : null,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      subColor:
        dashboard?.avgNpsScore != null
          ? dashboard.avgNpsScore >= 9
            ? 'text-green-600'
            : dashboard.avgNpsScore >= 7
              ? 'text-yellow-600'
              : 'text-red-600'
          : '',
    },
    {
      label: t('surveys.statCards.surveysCreated'),
      value: dashboard?.surveysCreated ?? 0,
      icon: Users,
      sub: null,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageTitle title={t('surveys.dashboardTitle')} subtitle={t('surveys.dashboardSubtitle')} />
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
        >
          <PlusCircle className="w-4 h-4" />
          {t('surveys.createASurvey')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{card.label}</p>
              <div className={`w-8 h-8 ${card.bg} rounded-lg flex items-center justify-center`}>
                <card.icon className={`w-4 h-4 ${card.color}`} />
              </div>
            </div>
            {dashboardLoading ? (
              <div className="h-7 w-16 bg-gray-100 rounded animate-pulse" />
            ) : (
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            )}
            {card.sub && <p className={`text-xs ${card.subColor ?? 'text-gray-400'}`}>{card.sub}</p>}
          </div>
        ))}
      </div>

      {/* Surveys list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('surveys.yourSurveys')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('surveys.yourSurveysSubtitle')}</p>
          </div>
          {surveys.length > 0 && (
            <div className="relative flex-shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder={t('surveys.filterByName')}
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 w-48"
              />
            </div>
          )}
        </div>

        {surveysLoading ? (
          <div className="divide-y divide-gray-100">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="px-6 py-4 flex items-center gap-4">
                <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-16 bg-gray-100 rounded animate-pulse ml-auto" />
              </div>
            ))}
          </div>
        ) : surveys.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">{t('surveys.noSurveysYet')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('surveys.noSurveysHelp')}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              {t('surveys.createSurvey')}
            </button>
          </div>
        ) : (() => {
          const filtered = surveys.filter((s) => s.title.toLowerCase().includes(filterQuery.toLowerCase()));
          if (filtered.length === 0) return (
            <div className="text-center py-10">
              <p className="text-sm text-gray-500">{t('surveys.noSurveysMatch')} <strong>"{filterQuery}"</strong></p>
              <button onClick={() => setFilterQuery('')} className="mt-2 text-xs text-purple-600 hover:underline">{t('surveys.clearFilter')}</button>
            </div>
          );
          return (
          <div className="divide-y divide-gray-100">
            {filtered.map((survey) => (
              <div
                key={survey.id}
                className={`flex items-center gap-4 px-6 py-4 transition-colors cursor-pointer ${
                  selectedSurveyId === survey.id ? 'bg-purple-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedSurveyId(selectedSurveyId === survey.id ? undefined : survey.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/survey/${survey.id}`); }}
                      className="text-sm font-semibold text-gray-900 truncate hover:text-purple-600 transition-colors text-left"
                    >
                      {survey.title}
                    </button>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_COLORS[survey.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {survey.status}
                    </span>
                  </div>
                  {survey.description && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{survey.description}</p>
                  )}
                </div>

                <div className="hidden md:flex items-center gap-6 text-center flex-shrink-0">
                  <div>
                    <p className="text-xs text-gray-400">{t('surveys.columns.questions')}</p>
                    <p className="text-sm font-semibold text-gray-700">{survey.questionCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">{t('surveys.columns.responses')}</p>
                    <p className="text-sm font-semibold text-gray-700">{survey.responsesCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">{t('surveys.columns.created')}</p>
                    <p className="text-sm font-medium text-gray-600">
                      {new Date(survey.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate(`/survey/${survey.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Users className="w-3 h-3" />
                    {t('surveys.participants')}
                  </button>
                  <button
                    onClick={() => navigate(`/survey/questions/${survey.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Edit3 className="w-3 h-3" />
                    {t('surveys.edit')}
                  </button>
                  <button
                    onClick={() => handleCopySurveyLink(survey.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Link className="w-3 h-3" />
                    {t('surveys.copyLink')}
                  </button>
                  <button
                    onClick={() => navigate(`/survey/send/${survey.id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Send className="w-3 h-3" />
                    {t('common.send')}
                  </button>
                  <button
                    onClick={() => setDeletingSurveyId(survey.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    {t('surveys.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>

      {/* Survey Responses */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {t('surveys.surveyResponses')}
              {selectedSurveyId && surveys.find(s => s.id === selectedSurveyId) && (
                <span className="ml-2 text-sm font-normal text-purple-600">
                  — {surveys.find(s => s.id === selectedSurveyId)?.title}
                </span>
              )}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {selectedSurveyId ? `${t('surveys.clickSurveyToSwitch')} · ` : ''}
              {t('surveys.clickToViewDetails')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedSurveyId && (
              <button
                onClick={() => setSelectedSurveyId(undefined)}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                {t('surveys.showAll')}
              </button>
            )}
            <button
              onClick={handleExportCsv}
              disabled={copyingLink}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              {t('surveys.exportCsv')}
            </button>
          </div>
        </div>

        {responsesLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-44 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : responses.length === 0 ? (
          <div className="text-center py-14">
            <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium text-sm">{t('surveys.noResponsesYet')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('surveys.noResponsesHelp')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {responses.map((response) => (
              <SurveyResponseCard
                key={response.id}
                response={response}
                onClick={() => navigate(`/survey/response/${response.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateSurveyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(surveyId) => {
            setShowCreateModal(false);
            navigate(`/survey/${surveyId}`);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingSurveyId}
        onClose={() => setDeletingSurveyId(null)}
        onConfirm={handleDeleteSurvey}
        title={t('surveys.deleteSurveyTitle')}
        description={t('surveys.deleteSurveyDesc')}
        confirmLabel={t('surveys.deleteButton')}
        variant="danger"
        isLoading={deletingLoading}
      />
    </div>
  );
};

export default SurveyDashboard;
